import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import {Users, Copy, Share2, Gift, CheckCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

export function ReferralSystem() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<any>(null)
  const [referralCode, setReferralCode] = useState('')
  const [referralCount, setReferralCount] = useState(0)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (user) {
      loadProfile()
    }
  }, [user])

  const loadProfile = async () => {
    if (!user) return
    try {
      const { list } = await lumi.entities.profiles.list({
        filter: { userId: user.userId }
      })
      if (list.length > 0) {
        const userProfile = list[0]
        setProfile(userProfile)
        
        // Generate referral code if doesn't exist
        if (!userProfile.referralCode) {
          const code = generateReferralCode(userProfile.displayName, user.userId)
          await lumi.entities.profiles.update(userProfile._id, {
            referralCode: code
          })
          setReferralCode(code)
        } else {
          setReferralCode(userProfile.referralCode)
        }
        
        // Count referrals
        const { list: referrals } = await lumi.entities.profiles.list({
          filter: { referredBy: user.userId }
        })
        setReferralCount(referrals.length)
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
    }
  }

  const generateReferralCode = (name: string, userId: string): string => {
    const cleanName = name.split(' ')[0].toUpperCase().slice(0, 4)
    const randomPart = userId.slice(-4).toUpperCase()
    return `${cleanName}${randomPart}`
  }

  const getReferralLink = () => {
    return `${window.location.origin}/?ref=${referralCode}`
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getReferralLink())
    setCopied(true)
    toast.success('Link copiado!')
    setTimeout(() => setCopied(false), 2000)
  }

  const shareReferral = () => {
    const message = `🩸 Junte-se a mim no Doe e ajude a salvar vidas!\n\nCada cadastro conta. Use meu código: ${referralCode}\n\n${getReferralLink()}\n\n💝 Ganhe +50 pontos ao se cadastrar com meu convite!`
    
    if (navigator.share) {
      navigator.share({
        title: 'Convite Doe',
        text: message
      }).catch(() => {
        navigator.clipboard.writeText(message)
        toast.success('Mensagem copiada!')
      })
    } else {
      navigator.clipboard.writeText(message)
      toast.success('Mensagem copiada!')
    }
  }

  if (!user || !profile) return null

  return (
    <Card className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-white">
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <Gift className="h-6 w-6 text-red-600 mr-2" />
          Convide Amigos e Ganhe Pontos
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-white rounded-lg p-4 border border-red-200">
          <p className="text-sm text-gray-700 mb-3">
            <strong>Como funciona:</strong>
          </p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              Você ganha <strong className="text-red-600">+75 pontos</strong> por cada amigo que se cadastrar
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              Seu amigo ganha <strong className="text-red-600">+50 pontos</strong> de bônus ao usar seu código
            </li>
            <li className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              Cada indicação da mesma cidade te dá pontos extras no ranking municipal
            </li>
          </ul>
        </div>

        <div className="bg-red-600 rounded-lg p-6 text-center text-white">
          <p className="text-sm font-medium mb-2">Seu Código de Convite</p>
          <p className="text-3xl font-black mb-4 tracking-wider">{referralCode}</p>
          <div className="flex items-center justify-center space-x-2 text-sm">
            <Users className="h-4 w-4" />
            <span>{referralCount} {referralCount === 1 ? 'pessoa convidada' : 'pessoas convidadas'}</span>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Link de Convite</label>
          <div className="flex space-x-2">
            <Input 
              value={getReferralLink()} 
              readOnly 
              className="text-sm"
            />
            <Button 
              onClick={copyToClipboard}
              variant="outline"
              className="flex-shrink-0"
            >
              {copied ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <Button 
          onClick={shareReferral}
          className="w-full bg-red-600 hover:bg-red-700 text-white"
        >
          <Share2 className="h-4 w-4 mr-2" />
          Compartilhar Convite
        </Button>

        <p className="text-xs text-center text-gray-500">
          Quanto mais pessoas você trouxer, mais vidas podemos salvar juntos! 💝
        </p>
      </CardContent>
    </Card>
  )
}
